import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Keyboard,
  RefreshCw,
  Search,
  ShieldQuestion,
} from 'lucide-react';
import { Link } from 'react-router';
import {
  Alert,
  Badge,
  Barcode,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Gtin,
  Input,
  Loading,
  Meter,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Segmented,
  Select,
  cn,
} from '../ui/index.js';
import type { AlertTone, Tone } from '../ui/index.js';
import { ApiError, fetchJson } from '../lib/fetch-json.js';
import { stamp, toCapitalizedCase } from '../lib/format.js';
import { isValidGtin, repairGtinCheckDigit, toEan13 } from '../lib/gtin.js';

type StatusView = 'open' | 'resolved' | 'wontfix';
type SortView = 'oldest' | 'recent' | 'kind' | 'code';
type ViewMode = 'list' | 'triage';
type BulkAction = 'resolve' | 'wontfix';

interface ConflictProduct {
  readonly wcProductId: number;
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly barcodeRaw: string | null;
}

interface ConflictItem {
  readonly id: number;
  readonly kind: string;
  readonly code: string | null;
  readonly checksumOk: boolean | null;
  readonly status: string;
  readonly detail: unknown;
  readonly resolution: string | null;
  readonly resolvedBy: string | null;
  readonly resolvedAt: string | null;
  readonly products: readonly ConflictProduct[];
  readonly createdAt: string;
}

interface ConflictsPayload {
  readonly items: readonly ConflictItem[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly counts: {
    readonly open: number;
    readonly resolved: number;
    readonly wontfix: number;
    readonly byKind: Readonly<Record<string, number>>;
  };
}

interface KindMeta {
  readonly label: string;
  readonly short: string;
  readonly hint: string;
  readonly tone: Tone;
}

interface Suggestion {
  readonly code: string;
  readonly source: 'sources' | 'checksum' | 'field';
  readonly label: string;
}

const KIND_META: Record<string, KindMeta> = {
  fields_disagree: {
    label: 'Champs GTIN en désaccord',
    short: 'Champs en désaccord',
    hint: 'Deux champs GTIN du même produit se contredisent : gardez celui qui est corroboré par une source.',
    tone: 'scan',
  },
  dup_across_skus: {
    label: 'Code partagé (variantes)',
    short: 'Code partagé',
    hint: 'Deux SKUs portent le même code : variantes du même article ou doublon de saisie.',
    tone: 'neutral',
  },
  reuse_reference: {
    label: 'Référence fournisseur en GTIN',
    short: 'Référence fournisseur',
    hint: 'Une référence fournisseur a été saisie comme code-barres : remplacez-la par le vrai GTIN.',
    tone: 'signal',
  },
  bad_checksum: {
    label: 'Clé de contrôle invalide',
    short: 'Clé invalide',
    hint: 'La clé de contrôle ne tombe pas juste : la clé mod-10 peut être recalculée puis adoptée.',
    tone: 'alarm',
  },
  bad_length: {
    label: 'Longueur impossible',
    short: 'Longueur impossible',
    hint: 'Seuls EAN-8, EAN-13, UPC-12 et GTIN-14 sont acceptés — le code doit être ressaisi ou scanné.',
    tone: 'signal',
  },
  fabricated: {
    label: 'Code inventé',
    short: 'Code inventé',
    hint: 'Code séquentiel inventé : à remplacer, jamais à conserver en diffusion.',
    tone: 'alarm',
  },
  suspect_wrong: {
    label: 'Code suspect (autre GTIN proposé)',
    short: 'Code suspect',
    hint: 'Une source concordante propose un autre GTIN : arbitrez avant diffusion, le code proposé est prêt à adopter.',
    tone: 'signal',
  },
};

const KIND_ORDER = [
  'suspect_wrong',
  'bad_checksum',
  'fields_disagree',
  'reuse_reference',
  'dup_across_skus',
  'bad_length',
  'fabricated',
] as const;

const PAGE_STEP = 100;
const PAGE_MAX = 500;

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function normalizeCandidate(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return null;
  const ean = toEan13(digits);
  if (ean !== null && isValidGtin(ean)) return ean;
  return isValidGtin(digits) ? digits : null;
}

function suggestionsFor(conflict: ConflictItem): readonly Suggestion[] {
  const detail = asRecord(conflict.detail);
  const out: Suggestion[] = [];

  if (conflict.kind === 'suspect_wrong') {
    const own = asString(detail.ownCode)?.replace(/\D/g, '') ?? '';
    const proposed = asString(detail.proposed) ?? '';
    const candidate = normalizeCandidate(proposed);
    if (candidate !== null && candidate !== own) {
      out.push({
        code: candidate,
        source: 'sources',
        label: 'Proposé par les sources concordantes',
      });
    }
  }

  if (conflict.kind === 'bad_checksum' && conflict.code !== null) {
    const repaired = repairGtinCheckDigit(conflict.code);
    if (repaired !== null && repaired !== conflict.code) {
      out.push({
        code: repaired,
        source: 'checksum',
        label: 'Clé de contrôle recalculée (mod-10)',
      });
    }
  }

  if (conflict.kind === 'fields_disagree') {
    const text = asString(detail.detail) ?? '';
    const labels = ['Champ GTIN', 'Métadonnée EAN'];
    let position = 0;
    for (const match of text.matchAll(/"([^"]+)"/g)) {
      const raw = match[1];
      if (raw === undefined) continue;
      const candidate = normalizeCandidate(raw);
      if (candidate !== null && !out.some((entry) => entry.code === candidate)) {
        out.push({
          code: candidate,
          source: 'field',
          label: labels[position] ?? 'Champ GTIN',
        });
        position += 1;
      }
    }
  }

  return out;
}

function detailTextFor(conflict: ConflictItem): string | null {
  const detail = asRecord(conflict.detail);
  const text = asString(detail.detail);
  if (text !== null && text !== '') return text;
  if (conflict.kind === 'suspect_wrong') {
    const own = asString(detail.ownCode);
    const score = typeof detail.score === 'number' ? detail.score : null;
    const parts: string[] = [];
    if (own !== null) parts.push(`code actuel ${own}`);
    if (score !== null) parts.push(`score ${score.toFixed(2)}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  }
  return null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Erreur inconnue';
}

function Kbd({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <kbd className="rounded border border-border bg-cream px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink-soft">
      {children}
    </kbd>
  );
}

function CopyButton({
  value,
  copied,
  onCopy,
  label,
}: {
  readonly value: string;
  readonly copied: boolean;
  readonly onCopy: (value: string) => void;
  readonly label: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => onCopy(value)}
      className={cn(
        'grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-faint transition-colors hover:bg-cream hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-herb-500/40',
        copied && 'text-herb-700',
      )}
      aria-label={label}
      title={label}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

function ProductLine({
  product,
  copiedValue,
  onCopy,
}: {
  readonly product: ConflictProduct;
  readonly copiedValue: string | null;
  readonly onCopy: (value: string) => void;
}): React.ReactElement {
  const target = product.sku ?? product.name;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5 text-xs">
      <span className="w-24 shrink-0 font-mono text-[11px] font-semibold text-ink-soft">
        {product.sku ?? '∅'}
      </span>
      <Link
        to={`/products?q=${encodeURIComponent(target)}`}
        className="inline-flex min-w-0 flex-1 items-center gap-1.5 truncate text-xs font-medium text-ink underline-offset-2 hover:text-herb-700 hover:underline sm:text-sm"
        title="Ouvrir la fiche produit"
      >
        <span className="truncate">{toCapitalizedCase(product.name)}</span>
        <ExternalLink className="h-3 w-3 shrink-0 text-ink-ghost" aria-hidden="true" />
      </Link>
      <span className="text-xs text-ink-soft">{product.brand ?? '—'}</span>
      <span className="inline-flex items-center gap-2">
        <Gtin gtin={product.barcodeRaw} tone="neutral" empty="∅" />
        {product.barcodeRaw !== null && product.barcodeRaw !== '' && (
          <CopyButton
            value={product.barcodeRaw}
            copied={copiedValue === product.barcodeRaw}
            onCopy={onCopy}
            label={`Copier le code-barres de ${product.sku ?? product.name}`}
          />
        )}
      </span>
    </li>
  );
}

interface ConflictCardProps {
  readonly conflict: ConflictItem;
  readonly suggestions: readonly Suggestion[];
  readonly draft: string;
  readonly busy: boolean;
  readonly correcting: boolean;
  readonly error: string | null;
  readonly selected: boolean;
  readonly focused: boolean;
  readonly triage: boolean;
  readonly copiedValue: string | null;
  readonly inputRef: (element: HTMLInputElement | null) => void;
  readonly onFocus: () => void;
  readonly onToggle: () => void;
  readonly onResolve: (wontfix: boolean) => void;
  readonly onDraftChange: (value: string) => void;
  readonly onCorrect: () => void;
  readonly onAdopt: (code: string) => void;
  readonly onCopy: (value: string) => void;
}

function ConflictCard({
  conflict,
  suggestions,
  draft,
  busy,
  correcting,
  error,
  selected,
  focused,
  triage,
  copiedValue,
  inputRef,
  onFocus,
  onToggle,
  onResolve,
  onDraftChange,
  onCorrect,
  onAdopt,
  onCopy,
}: ConflictCardProps): React.ReactElement {
  const meta = KIND_META[conflict.kind] ?? {
    label: conflict.kind,
    short: conflict.kind,
    hint: 'Arbitrez selon les preuves des sources (docs/04 §7).',
    tone: 'neutral' as const,
  };
  const detailText = detailTextFor(conflict);
  const isOpen = conflict.status === 'open';
  const primarySuggestion = suggestions[0] ?? null;

  return (
    <article
      id={`conflict-${conflict.id}`}
      onFocus={onFocus}
      onMouseDown={onFocus}
      className={cn(
        'overflow-hidden rounded-card border bg-card shadow-xs transition-all',
        focused ? 'border-herb-600/50 ring-2 ring-herb-500/25' : 'border-border',
        triage && 'shadow-card',
      )}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-cream/40 px-4 py-2.5">
        {isOpen && !triage && (
          <Checkbox
            checked={selected}
            onChange={onToggle}
            label={<span className="sr-only">Sélectionner le conflit #{conflict.id}</span>}
          />
        )}
        <Badge tone={meta.tone}>{meta.label}</Badge>
        <span className="inline-flex items-center gap-1.5">
          <Barcode value={conflict.code} tone="scan" height={16} />
          <span className="font-mono text-xs font-semibold text-sky-800 sm:text-sm">
            {conflict.code ?? '∅'}
          </span>
          {conflict.code !== null && conflict.code !== '' && (
            <CopyButton
              value={conflict.code}
              copied={copiedValue === conflict.code}
              onCopy={onCopy}
              label={`Copier le GTIN ${conflict.code}`}
            />
          )}
          {conflict.checksumOk === false && <Badge tone="alarm">clé KO</Badge>}
        </span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-faint">
          #{conflict.id} · {stamp(conflict.createdAt)}
        </span>
        {isOpen ? (
          <div className="flex gap-2">
            <Button
              size="xs"
              variant="primary"
              disabled={busy}
              onClick={() => onResolve(false)}
              title="Marquer résolu (Y)"
            >
              {busy ? 'En cours…' : 'Résolu'}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={busy}
              onClick={() => onResolve(true)}
              title="Ignorer (W)"
            >
              Ignorer
            </Button>
          </div>
        ) : (
          <Badge tone={conflict.status === 'resolved' ? 'good' : 'muted'}>
            {conflict.status === 'resolved' ? 'Résolu' : 'Ignoré'}
          </Badge>
        )}
      </header>

      <div className="grid gap-4 px-4 py-3.5 lg:grid-cols-[minmax(0,1fr)_minmax(260px,340px)]">
        <div className="min-w-0">
          <p className="text-xs leading-relaxed text-ink-soft">{meta.hint}</p>
          {detailText !== null && (
            <p className="mt-1 font-mono text-[11px] text-ink-faint">{detailText}</p>
          )}

          {suggestions.length > 0 && isOpen && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-ink-soft">
                <ShieldQuestion className="h-3.5 w-3.5 text-sky-700" aria-hidden="true" />
                Suggestion{primarySuggestion !== null && suggestions.length > 1 ? 's' : ''}
              </span>
              {suggestions.map((suggestion) => (
                <span
                  key={`${suggestion.source}-${suggestion.code}`}
                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-cream/60 py-1 pl-2 pr-1"
                  title={suggestion.label}
                >
                  <span className="font-mono text-[11px] font-semibold text-ink">
                    {suggestion.code}
                  </span>
                  <span className="hidden text-[10px] text-ink-faint sm:inline">
                    {suggestion.label}
                  </span>
                  <Button
                    size="xs"
                    variant="soft"
                    className="h-6 px-2"
                    onClick={() => onAdopt(suggestion.code)}
                    title={`Préparer la correction vers ${suggestion.code}`}
                  >
                    Adopter
                  </Button>
                </span>
              ))}
            </div>
          )}

          <ul className="mt-2 divide-y divide-border">
            {conflict.products.map((product) => (
              <ProductLine
                key={product.wcProductId}
                product={product}
                copiedValue={copiedValue}
                onCopy={onCopy}
              />
            ))}
          </ul>

          {!isOpen && (
            <div className="mt-2 rounded-lg border border-border bg-cream/40 px-3 py-2 text-xs text-ink-soft">
              <p>
                <span className="font-semibold text-ink">Résolution :</span>{' '}
                {conflict.resolution ?? '—'}
              </p>
              <p className="mt-0.5 text-[11px] text-ink-faint">
                {conflict.resolvedBy ?? '—'} · {stamp(conflict.resolvedAt)}
              </p>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="flex flex-col gap-2 self-start rounded-lg border border-border bg-cream/30 p-3">
            <label
              htmlFor={`gtin-${conflict.id}`}
              className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft"
            >
              Corriger le GTIN du produit principal
            </label>
            <div className="flex items-center gap-1.5">
              <Input
                id={`gtin-${conflict.id}`}
                ref={inputRef}
                value={draft}
                onChange={(event) => onDraftChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    onCorrect();
                  }
                }}
                placeholder={
                  primarySuggestion !== null ? `Suggéré : ${primarySuggestion.code}` : 'Nouveau GTIN…'
                }
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                aria-label={`Nouveau GTIN pour le conflit ${conflict.id}`}
                className="h-8 px-2.5 font-mono text-xs"
              />
              <Button
                type="button"
                variant="accent"
                size="sm"
                onClick={onCorrect}
                disabled={correcting || draft.trim() === ''}
              >
                {correcting ? '…' : 'Corriger'}
              </Button>
            </div>
            {error !== null && (
              <p role="alert" className="text-[11px] leading-relaxed text-rust-700">
                {error}
              </p>
            )}
            <p className="text-[10px] leading-relaxed text-ink-ghost">
              Applique au premier produit listé, force le transfert si le code est déjà attribué et
              journalise la décision. <Kbd>Entrée</Kbd> valide.
            </p>
          </div>
        )}
      </div>
    </article>
  );
}

/** S03 — Conflicts (docs/09 §3): arbitration queue for the GTIN defects. */
export function ConflictsPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusView>('open');
  const [kind, setKind] = useState<string>('all');
  const [sort, setSort] = useState<SortView>('oldest');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [suggestionsOnly, setSuggestionsOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE_STEP);
  const [view, setView] = useState<ViewMode>(() =>
    window.localStorage.getItem('conflicts:view') === 'triage' ? 'triage' : 'list',
  );
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [notice, setNotice] = useState<{ readonly tone: AlertTone; readonly text: string } | null>(
    null,
  );
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const inputRefs = useRef(new Map<number, HTMLInputElement>());

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLimit(PAGE_STEP);
  }, [status, kind, query, sort]);

  useEffect(() => {
    window.localStorage.setItem('conflicts:view', view);
  }, [view]);

  useEffect(() => {
    if (status !== 'open' && view === 'triage') setView('list');
  }, [status, view]);

  useEffect(() => {
    if (notice === null) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const conflictsQuery = useQuery<ConflictsPayload>({
    queryKey: ['conflicts', status, kind, query, sort, limit],
    queryFn: () => {
      const params = new URLSearchParams({
        status,
        sort,
        limit: String(limit),
      });
      if (kind !== 'all') params.set('kind', kind);
      if (query !== '') params.set('q', query);
      return fetchJson<ConflictsPayload>(`/api/v1/review/conflicts?${params.toString()}`);
    },
    placeholderData: (previous) => previous,
  });

  const items = conflictsQuery.data?.items ?? [];
  const counts = conflictsQuery.data?.counts ?? {
    open: 0,
    resolved: 0,
    wontfix: 0,
    byKind: {},
  };
  const total = conflictsQuery.data?.total ?? 0;

  const suggestionsById = useMemo(
    () => new Map(items.map((conflict) => [conflict.id, suggestionsFor(conflict)])),
    [items],
  );

  const visible = useMemo(() => {
    if (!suggestionsOnly) return items;
    return items.filter((conflict) => (suggestionsById.get(conflict.id)?.length ?? 0) > 0);
  }, [items, suggestionsById, suggestionsOnly]);

  const suggestibleCount = useMemo(
    () => items.filter((conflict) => (suggestionsById.get(conflict.id)?.length ?? 0) > 0).length,
    [items, suggestionsById],
  );

  const openVisible = useMemo(
    () => visible.filter((conflict) => conflict.status === 'open'),
    [visible],
  );

  const triageItems = openVisible;
  const current =
    triageItems.find((conflict) => conflict.id === focusedId) ?? triageItems[0] ?? null;
  const currentIndex = current === null ? -1 : triageItems.indexOf(current);

  useEffect(() => {
    const openIds = new Set(items.filter((conflict) => conflict.status === 'open').map((c) => c.id));
    setSelected((previous) => {
      if (previous.size === 0) return previous;
      const next = new Set([...previous].filter((id) => openIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [items]);

  useEffect(() => {
    if (triageItems.length === 0) {
      if (focusedId !== null) setFocusedId(null);
      return;
    }
    if (focusedId === null || !triageItems.some((conflict) => conflict.id === focusedId)) {
      setFocusedId(triageItems[0]?.id ?? null);
    }
  }, [triageItems, focusedId]);

  const resolveOne = useMutation({
    mutationFn: (variables: { readonly id: number; readonly wontfix: boolean }) =>
      fetchJson<{ readonly status: string }>(`/api/v1/review/conflicts/${variables.id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({
          resolution: variables.wontfix ? 'ignoré depuis S03' : 'résolu depuis S03',
          wontfix: variables.wontfix,
        }),
      }),
    onMutate: (variables) => {
      setRowErrors((previous) => {
        if (previous[variables.id] === undefined) return previous;
        const next = { ...previous };
        delete next[variables.id];
        return next;
      });
    },
    onSuccess: (_data, variables) => {
      setSelected((previous) => {
        if (!previous.has(variables.id)) return previous;
        const next = new Set(previous);
        next.delete(variables.id);
        return next;
      });
      setNotice({
        tone: 'good',
        text: variables.wontfix ? `Conflit #${variables.id} ignoré.` : `Conflit #${variables.id} résolu.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['conflicts'] });
    },
    onError: (error, variables) => {
      setRowErrors((previous) => ({ ...previous, [variables.id]: errorMessage(error) }));
    },
  });

  const correct = useMutation({
    mutationFn: (variables: { readonly id: number; readonly newGtin: string }) =>
      fetchJson<{ readonly status: string; readonly resolvedWith: string }>(
        `/api/v1/review/conflicts/${variables.id}/resolve`,
        { method: 'POST', body: JSON.stringify({ newGtin: variables.newGtin }) },
      ),
    onMutate: (variables) => {
      setRowErrors((previous) => {
        if (previous[variables.id] === undefined) return previous;
        const next = { ...previous };
        delete next[variables.id];
        return next;
      });
    },
    onSuccess: (result, variables) => {
      setDrafts((previous) => {
        if (previous[variables.id] === undefined) return previous;
        const next = { ...previous };
        delete next[variables.id];
        return next;
      });
      setSelected((previous) => {
        if (!previous.has(variables.id)) return previous;
        const next = new Set(previous);
        next.delete(variables.id);
        return next;
      });
      setNotice({
        tone: 'good',
        text: `Conflit #${variables.id} corrigé : GTIN ${result.resolvedWith} approuvé.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['conflicts'] });
    },
    onError: (error, variables) => {
      setRowErrors((previous) => ({ ...previous, [variables.id]: errorMessage(error) }));
    },
  });

  const bulk = useMutation({
    mutationFn: (variables: { readonly ids: readonly number[]; readonly action: BulkAction }) =>
      fetchJson<{ readonly updated: number; readonly requested: number; readonly skipped: number }>(
        '/api/v1/review/conflicts/bulk-resolve',
        {
          method: 'POST',
          body: JSON.stringify({ ids: [...variables.ids], action: variables.action }),
        },
      ),
    onSuccess: (result, variables) => {
      setSelected(new Set());
      setBulkAction(null);
      const verb = variables.action === 'wontfix' ? 'ignoré(s)' : 'résolu(s)';
      const skipped = result.skipped > 0 ? ` · ${result.skipped} déjà traité(s)` : '';
      setNotice({ tone: 'good', text: `${result.updated} conflit(s) ${verb}${skipped}.` });
      void queryClient.invalidateQueries({ queryKey: ['conflicts'] });
    },
    onError: (error) => {
      setBulkAction(null);
      setNotice({ tone: 'alarm', text: errorMessage(error) });
    },
  });

  const moveFocus = useCallback(
    (delta: number) => {
      const list = view === 'triage' ? triageItems : visible;
      if (list.length === 0) return;
      const index = list.findIndex((conflict) => conflict.id === focusedId);
      const nextIndex =
        index === -1
          ? delta > 0
            ? 0
            : list.length - 1
          : (index + delta + list.length) % list.length;
      const next = list[nextIndex];
      if (next === undefined) return;
      setFocusedId(next.id);
      const node = document.getElementById(`conflict-${next.id}`);
      node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },
    [view, triageItems, visible, focusedId],
  );

  const advanceFrom = useCallback(
    (id: number) => {
      const list = view === 'triage' ? triageItems : visible;
      const index = list.findIndex((conflict) => conflict.id === id);
      if (index === -1) return;
      const next = list[index + 1] ?? list[index - 1];
      if (next !== undefined) setFocusedId(next.id);
    },
    [view, triageItems, visible],
  );

  const toggleSelected = useCallback((id: number) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((previous) => {
      const ids = openVisible.map((conflict) => conflict.id);
      const all = ids.length > 0 && ids.every((id) => previous.has(id));
      return all ? new Set() : new Set(ids);
    });
  }, [openVisible]);

  const copy = useCallback((value: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedValue(value);
        window.setTimeout(() => {
          setCopiedValue((currentValue) => (currentValue === value ? null : currentValue));
        }, 1600);
      })
      .catch(() => {
        setNotice({ tone: 'warn', text: 'Copie impossible : autorisation presse-papiers refusée.' });
      });
  }, []);

  const adopt = useCallback((conflict: ConflictItem, code: string) => {
    setDrafts((previous) => ({ ...previous, [conflict.id]: code }));
    setRowErrors((previous) => {
      if (previous[conflict.id] === undefined) return previous;
      const next = { ...previous };
      delete next[conflict.id];
      return next;
    });
    const input = inputRefs.current.get(conflict.id);
    if (input !== undefined) {
      input.focus();
      input.select();
    }
  }, []);

  const submitCorrection = useCallback(
    (conflict: ConflictItem) => {
      const value = (drafts[conflict.id] ?? '').trim();
      if (value === '') return;
      correct.mutate(
        { id: conflict.id, newGtin: value },
        { onSuccess: () => advanceFrom(conflict.id) },
      );
    },
    [drafts, correct, advanceFrom],
  );

  const resolveConflict = useCallback(
    (conflict: ConflictItem, wontfix: boolean) => {
      resolveOne.mutate(
        { id: conflict.id, wontfix },
        { onSuccess: () => advanceFrom(conflict.id) },
      );
    },
    [resolveOne, advanceFrom],
  );

  const keyStateRef = useRef({
    view,
    visible,
    triageItems,
    focusedId,
    dialogOpen: bulkAction !== null,
    suggestionsById,
    moveFocus,
    toggleSelected,
    resolveConflict,
    adopt,
    submitCorrection,
    setView,
    setFocusedId,
  });
  keyStateRef.current = {
    view,
    visible,
    triageItems,
    focusedId,
    dialogOpen: bulkAction !== null,
    suggestionsById,
    moveFocus,
    toggleSelected,
    resolveConflict,
    adopt,
    submitCorrection,
    setView,
    setFocusedId,
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const state = keyStateRef.current;
      const isEditable =
        event.target instanceof HTMLElement &&
        (event.target.tagName === 'INPUT' ||
          event.target.tagName === 'TEXTAREA' ||
          event.target.tagName === 'SELECT' ||
          event.target.isContentEditable);

      if (isEditable) {
        if (event.key === 'Escape' && event.target instanceof HTMLElement) event.target.blur();
        return;
      }
      if (state.dialogOpen) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const list = state.view === 'triage' ? state.triageItems : state.visible;
      const currentConflict = list.find((conflict) => conflict.id === state.focusedId);

      switch (event.key) {
        case '/':
          event.preventDefault();
          state.setView('list');
          searchRef.current?.focus();
          break;
        case 'j':
        case 'ArrowDown':
          event.preventDefault();
          state.moveFocus(1);
          break;
        case 'k':
        case 'ArrowUp':
          event.preventDefault();
          state.moveFocus(-1);
          break;
        case 'x':
          if (currentConflict !== undefined && currentConflict.status === 'open') {
            event.preventDefault();
            state.toggleSelected(currentConflict.id);
          }
          break;
        case 'y':
          if (currentConflict !== undefined && currentConflict.status === 'open') {
            event.preventDefault();
            state.resolveConflict(currentConflict, false);
          }
          break;
        case 'w':
          if (currentConflict !== undefined && currentConflict.status === 'open') {
            event.preventDefault();
            state.resolveConflict(currentConflict, true);
          }
          break;
        case 'a': {
          if (currentConflict === undefined) break;
          const suggestion = state.suggestionsById.get(currentConflict.id)?.[0];
          if (suggestion !== undefined) {
            event.preventDefault();
            state.adopt(currentConflict, suggestion.code);
          }
          break;
        }
        case 's':
          event.preventDefault();
          state.moveFocus(1);
          break;
        case 'Enter': {
          if (currentConflict === undefined) break;
          event.preventDefault();
          const input = inputRefs.current.get(currentConflict.id);
          if (input !== undefined) {
            input.focus();
            input.select();
          }
          break;
        }
        case 'Escape':
          state.setFocusedId(null);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const filtersActive =
    kind !== 'all' || search !== '' || query !== '' || sort !== 'oldest' || suggestionsOnly;

  const resetFilters = (): void => {
    setKind('all');
    setSearch('');
    setQuery('');
    setSort('oldest');
    setSuggestionsOnly(false);
  };

  const refresh = (): void => {
    void conflictsQuery.refetch();
  };

  const allSelected =
    openVisible.length > 0 && openVisible.every((conflict) => selected.has(conflict.id));

  return (
    <div>
      <PageHeader
        index="S03"
        title="Conflits GTIN"
        purpose="File d'arbitrage des codes-barres défectueux : champs contradictoires, codes partagés, références saisies en GTIN, clés de contrôle fausses. Corrigez en un clic avec les codes proposés, arbitrez en série et gardez la trace de chaque décision."
        badge={
          counts.open > 0 ? (
            <Badge tone="signal">{counts.open.toLocaleString('fr-FR')} à arbitrer</Badge>
          ) : (
            <Badge tone="good">file vide</Badge>
          )
        }
        meta={[
          { label: 'À arbitrer', value: counts.open.toLocaleString('fr-FR') },
          { label: 'Affichés', value: `${visible.length.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')}` },
          { label: 'Suggestions', value: suggestibleCount.toLocaleString('fr-FR') },
          ...(selected.size > 0
            ? [{ label: 'Sélection', value: selected.size.toLocaleString('fr-FR') }]
            : []),
        ]}
        actions={
          <>
            <Segmented<StatusView>
              ariaLabel="Statut des conflits"
              value={status}
              onChange={(next) => {
                setStatus(next);
                setSelected(new Set());
              }}
              options={[
                { value: 'open', label: 'À arbitrer', count: counts.open },
                { value: 'resolved', label: 'Résolus', count: counts.resolved },
                { value: 'wontfix', label: 'Ignorés', count: counts.wontfix },
              ]}
            />
            <Button
              variant="soft"
              size="sm"
              icon={<RefreshCw className={cn('h-3.5 w-3.5', conflictsQuery.isFetching && 'animate-spin')} />}
              onClick={refresh}
              disabled={conflictsQuery.isFetching}
            >
              Rafraîchir
            </Button>
          </>
        }
      />

      <Panel className="mb-4">
        <PanelBody className="space-y-3 py-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
                aria-hidden="true"
              />
              <Input
                ref={searchRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher : GTIN, SKU, nom, marque, n° de conflit…"
                aria-label="Rechercher dans les conflits"
                className="pl-9 pr-12"
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
                <Kbd>/</Kbd>
              </span>
            </div>
            <Select
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              aria-label="Filtrer par type de conflit"
              className="w-auto min-w-[190px]"
            >
              <option value="all">Tous les types</option>
              {KIND_ORDER.map((value) => (
                <option key={value} value={value}>
                  {KIND_META[value]?.short ?? value} ({(counts.byKind[value] ?? 0).toLocaleString('fr-FR')})
                </option>
              ))}
            </Select>
            <Select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortView)}
              aria-label="Trier les conflits"
              className="w-auto min-w-[160px]"
            >
              <option value="oldest">Plus anciens</option>
              <option value="recent">Plus récents</option>
              <option value="kind">Par type</option>
              <option value="code">Par GTIN</option>
            </Select>
            <Checkbox
              checked={suggestionsOnly}
              onChange={(event) => setSuggestionsOnly(event.target.checked)}
              label={`Avec suggestion (${suggestibleCount.toLocaleString('fr-FR')})`}
            />
            {filtersActive && (
              <Button variant="ghost" size="xs" onClick={resetFilters}>
                Réinitialiser
              </Button>
            )}
            {status === 'open' && (
              <Segmented<ViewMode>
                ariaLabel="Mode de travail"
                value={view}
                onChange={setView}
                options={[
                  { value: 'list', label: 'Liste' },
                  { value: 'triage', label: 'Triage' },
                ]}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-faint">
            <Keyboard className="h-3.5 w-3.5" aria-hidden="true" />
            <span>
              <Kbd>J</Kbd>/<Kbd>K</Kbd> naviguer
            </span>
            <span>
              <Kbd>X</Kbd> sélectionner
            </span>
            <span>
              <Kbd>Entrée</Kbd> corriger
            </span>
            <span>
              <Kbd>Y</Kbd> résolu · <Kbd>W</Kbd> ignorer
            </span>
            <span>
              <Kbd>A</Kbd> adopter la suggestion · <Kbd>S</Kbd> passer
            </span>
          </div>
        </PanelBody>
      </Panel>

      {conflictsQuery.isError && (
        <Alert tone="alarm" title="File indisponible" className="mb-4">
          {String(conflictsQuery.error.message)}
        </Alert>
      )}

      {notice !== null && (
        <Alert
          tone={notice.tone}
          title="Conflits GTIN"
          className="mb-4"
          actions={
            <Button variant="ghost" size="xs" onClick={() => setNotice(null)}>
              Fermer
            </Button>
          }
        >
          {notice.text}
        </Alert>
      )}

      {selected.size > 0 && (
        <div className="sticky top-2 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-card border border-herb-600/30 bg-herb-50/80 px-3 py-2 shadow-card backdrop-blur-xs">
          <Badge tone="good">{selected.size.toLocaleString('fr-FR')} sélectionné(s)</Badge>
          <Button
            variant="primary"
            size="xs"
            disabled={bulk.isPending}
            onClick={() => setBulkAction('resolve')}
          >
            Résoudre…
          </Button>
          <Button
            variant="ghost"
            size="xs"
            disabled={bulk.isPending}
            onClick={() => setBulkAction('wontfix')}
          >
            Ignorer…
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setSelected(new Set())}
            className="ml-auto"
          >
            Désélectionner
          </Button>
        </div>
      )}

      {conflictsQuery.isPending && <Loading label="Lecture de la file de conflits…" />}

      {conflictsQuery.data !== undefined && visible.length === 0 && (
        <EmptyState
          title={
            suggestionsOnly && items.length > 0
              ? 'Aucune suggestion dans cette page'
              : status === 'open'
                ? 'Aucun conflit à arbitrer'
                : 'Aucun conflit dans cet état'
          }
          hint={
            suggestionsOnly && items.length > 0
              ? `${items.length.toLocaleString('fr-FR')} conflit(s) chargé(s) mais aucun ne dispose d'un code réparable automatiquement. Désactivez le filtre pour les voir.`
              : status === 'open'
                ? 'Lancez un run de matching depuis S07 pour recalculer les candidats, ou revenez plus tard : la file se remplit automatiquement.'
                : 'Les conflits résolus et ignorés restent consultables ici avec leur auteur et leur motif.'
          }
          action={
            filtersActive || suggestionsOnly ? (
              <Button variant="soft" size="sm" onClick={resetFilters}>
                Réinitialiser les filtres
              </Button>
            ) : undefined
          }
        />
      )}

      {view === 'list' && visible.length > 0 && (
        <>
          {status === 'open' && (
            <div className="mb-2 flex items-center gap-2 px-1">
              <Checkbox
                checked={allSelected}
                onChange={toggleAll}
                label={
                  <span>
                    Tout sélectionner les {openVisible.length.toLocaleString('fr-FR')} conflits
                    affichés
                  </span>
                }
              />
            </div>
          )}
          <div className="space-y-3">
            {visible.map((conflict) => (
              <ConflictCard
                key={conflict.id}
                conflict={conflict}
                suggestions={suggestionsById.get(conflict.id) ?? []}
                draft={drafts[conflict.id] ?? ''}
                busy={resolveOne.isPending && resolveOne.variables?.id === conflict.id}
                correcting={correct.isPending && correct.variables?.id === conflict.id}
                error={rowErrors[conflict.id] ?? null}
                selected={selected.has(conflict.id)}
                focused={focusedId === conflict.id}
                copiedValue={copiedValue}
                triage={false}
                inputRef={(element) => {
                  if (element === null) inputRefs.current.delete(conflict.id);
                  else inputRefs.current.set(conflict.id, element);
                }}
                onFocus={() => setFocusedId(conflict.id)}
                onToggle={() => toggleSelected(conflict.id)}
                onResolve={(wontfix) => resolveConflict(conflict, wontfix)}
                onDraftChange={(value) =>
                  setDrafts((previous) => ({ ...previous, [conflict.id]: value }))
                }
                onCorrect={() => submitCorrection(conflict)}
                onAdopt={(code) => adopt(conflict, code)}
                onCopy={copy}
              />
            ))}
          </div>
        </>
      )}

      {view === 'triage' && status === 'open' && current !== null && (
        <Panel>
          <PanelHeader
            title="Mode triage"
            hint={`${(currentIndex + 1).toLocaleString('fr-FR')} / ${triageItems.length.toLocaleString('fr-FR')} affichés · ${counts.open.toLocaleString('fr-FR')} à arbitrer au total`}
            actions={
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => moveFocus(-1)}
                  aria-label="Conflit précédent"
                  icon={<ChevronLeft className="h-4 w-4" />}
                />
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => moveFocus(1)}
                  aria-label="Conflit suivant"
                  icon={<ChevronRight className="h-4 w-4" />}
                />
              </div>
            }
          />
          <div className="px-5 pt-4">
            <Meter
              value={
                triageItems.length === 0 ? 0 : (currentIndex + 1) / Math.max(triageItems.length, 1)
              }
              tone="scan"
            />
          </div>
          <PanelBody>
            <ConflictCard
              conflict={current}
              suggestions={suggestionsById.get(current.id) ?? []}
              draft={drafts[current.id] ?? ''}
              busy={resolveOne.isPending && resolveOne.variables?.id === current.id}
              correcting={correct.isPending && correct.variables?.id === current.id}
              error={rowErrors[current.id] ?? null}
              selected={selected.has(current.id)}
              focused={focusedId === current.id}
              copiedValue={copiedValue}
              triage
              inputRef={(element) => {
                if (element === null) inputRefs.current.delete(current.id);
                else inputRefs.current.set(current.id, element);
              }}
              onFocus={() => setFocusedId(current.id)}
              onToggle={() => toggleSelected(current.id)}
              onResolve={(wontfix) => resolveConflict(current, wontfix)}
              onDraftChange={(value) =>
                setDrafts((previous) => ({ ...previous, [current.id]: value }))
              }
              onCorrect={() => submitCorrection(current)}
              onAdopt={(code) => adopt(current, code)}
              onCopy={copy}
            />
          </PanelBody>
        </Panel>
      )}

      {view === 'triage' && status === 'open' && current === null && (
        <EmptyState
          title="File de triage vide"
          hint="Tous les conflits affichés sont traités. Désactivez les filtres ou rechargez la file pour continuer."
          action={
            <Button variant="soft" size="sm" onClick={resetFilters}>
              Réinitialiser les filtres
            </Button>
          }
        />
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-ink-faint">
          {items.length.toLocaleString('fr-FR')} chargé(s) sur {total.toLocaleString('fr-FR')}
          {suggestionsOnly && items.length !== visible.length
            ? ` · ${visible.length.toLocaleString('fr-FR')} après filtre suggestion`
            : ''}
        </p>
        {items.length < total && limit < PAGE_MAX && (
          <Button
            variant="soft"
            size="sm"
            onClick={() => setLimit((previous) => Math.min(PAGE_MAX, previous + PAGE_STEP))}
            disabled={conflictsQuery.isFetching}
          >
            Charger {Math.min(PAGE_STEP, PAGE_MAX - limit)} de plus
          </Button>
        )}
        {items.length < total && limit >= PAGE_MAX && (
          <p className="text-[11px] text-ink-faint">
            Limite de {PAGE_MAX} atteinte — affinez la recherche ou le type.
          </p>
        )}
      </div>

      <ConfirmDialog
        open={bulkAction !== null}
        title={
          bulkAction === 'wontfix'
            ? `Ignorer ${selected.size.toLocaleString('fr-FR')} conflit(s) ?`
            : `Résoudre ${selected.size.toLocaleString('fr-FR')} conflit(s) ?`
        }
        confirmLabel={
          bulkAction === 'wontfix'
            ? `Ignorer ${selected.size.toLocaleString('fr-FR')}`
            : `Résoudre ${selected.size.toLocaleString('fr-FR')}`
        }
        tone={bulkAction === 'wontfix' ? 'alarm' : 'signal'}
        busy={bulk.isPending}
        onCancel={() => setBulkAction(null)}
        onConfirm={() => {
          if (bulkAction === null || selected.size === 0) return;
          bulk.mutate({ ids: [...selected], action: bulkAction });
        }}
      >
        <p>
          {bulkAction === 'wontfix'
            ? 'Ces conflits sortiront de la file ouverte et seront marqués « ignorés » avec votre compte comme auteur.'
            : 'Ces conflits sortiront de la file ouverte et seront marqués « résolus » sans modification de code. Utilisez la correction GTIN quand un code doit être remplacé.'}
        </p>
        <p className="mt-2 font-mono text-xs text-ink-soft">
          {[...selected]
            .slice(0, 12)
            .map((id) => `#${id}`)
            .join(' · ')}
          {selected.size > 12 ? ` · +${selected.size - 12}` : ''}
        </p>
      </ConfirmDialog>
    </div>
  );
}
